<script setup lang="ts">
import { ref, nextTick, onMounted } from 'vue'
import ContentView from './ContentView.vue'
import SalesRepContactButton from '@/components/common/SalesRepContactButton.vue'
import ExternalLinks from '@/components/common/ExternalLinks.vue'
// import DataRefreshDatesButton from '@/components/common/DataRefreshDatesButton.vue'

const faqs = [
  {
    question: 'What does DBG stand for?',
    answer:
      'DBG stands for <strong>Dennis Bastas Group</strong>. It is the parent company of several businesses, including:<br><br>• <strong>Arrotex</strong> – Pharmaceutical business<br>• <strong>VidaCorp</strong> – Health, wellness, and beauty<br>• <strong>AXE Health Services</strong> – Telehealth and health consultancy<br>• <strong>Independent Pharmacies of Australia (IPA)</strong> – Pharmacy retail'
  },
  {
    question: 'How is my total DBG value calculated?',
    answer:
      'Your total DBG value is calculated as the combined amount you’ve earned across all DBG businesses. This may include discounted pricing, compliance rebates, marketing payments, sign-on bonuses, free stock allocations, and other financial or promotional benefits provided to you as part of your engagement with DBG.'
  },
  {
    question: 'What is the breakdown of my Arrotex and VidaCorp value?',
    answer:
      'Your value is made up of the following:<br><br>• <strong>Terms</strong> – The amount you saved through discounted pricing (gross sales minus what you actually paid).<br>• <strong>Rebates</strong> – Payments for meeting contract compliance.<br>• <strong>Bonus Stock</strong> – Value of extra stock provided under your agreement.<br>• <strong>Marketing Payments</strong> – Payments for participating in marketing activities.<br>• <strong>Sign-On Bonus</strong> – Bonus you received for signing on. If over $250K, it’s spread over your contract period.'
  },
  // {
  //   question: 'How is my Arrotex opportunity calculated?',
  //   answer:
  //     "Your Arrotex opportunity is based on 100% substitution of your usage from last month, multiplied by three. The total opportunity value includes the discounts you would receive, the rebates you're eligible for, and the bonus stock you’d get."
  // },
  {
    question: 'How is my Arrotex opportunity calculated?',
    answer:
      "Your Arrotex opportunity is based on 100% substitution of your usage from last three months. The total opportunity value includes the discounts you would receive, the rebates you're eligible for, and the bonus stock you’d get."
  },
  {
    question: 'How is my VidaCorp opportunity calculated?',
    answer: `Your VidaCorp opportunity is based on your store's distribution. It represents the estimated annual value a store of your size could earn by closing all distribution gaps.`
  },
  {
    question: 'How is my AXE opportunity calculated?',
    answer:
      'Your AXE opportunity is calculated by multiplying the average value of a claim by the number of claims you missed.'
  },
  {
    question: 'How often is the data refreshed?',
    answer:
      'Your <strong>Earned Value</strong> data is refreshed quarterly, once all incentive payments for the quarter are finalised. Your <strong>Opportunity</strong> data is updated monthly to reflect the latest opportunity gaps.'
  }
]

const openIndex = ref(-1)

const faqItems = ref<HTMLElement[]>([])
const collapsedHeight = '4.6875rem' // 75px

const toggleFaq = async (index: number) => {
  if (openIndex.value === index) {
    // Close the currently open FAQ
    const element = faqItems.value[index]!
    // Set height to actual scroll height before transitioning
    element.style.height = `${element.scrollHeight}px`
    // Force a reflow
    element.offsetHeight
    // Start transition to 0
    element.style.height = collapsedHeight
    openIndex.value = -1
  } else {
    // If another FAQ is open, close it first
    if (openIndex.value !== -1) {
      const prevElement = faqItems.value[openIndex.value]
      if (prevElement) {
        prevElement.style.height = collapsedHeight
      }
    }

    // Open the new FAQ
    openIndex.value = index
    await nextTick()
    const element = faqItems.value[index]
    if (element) {
      // Set the target height
      element.style.height = `${element.scrollHeight}px`
    }
  }
}

// Initialize FAQ items after component is mounted
const initializeFaqs = () => {
  // Set all FAQ items to initial height except the one that should be open
  faqItems.value.forEach((item, index) => {
    if (index === openIndex.value) {
      item.style.height = `${item.scrollHeight}px`
    } else {
      item.style.height = collapsedHeight
    }
  })
}

// Call initializeFaqs when the component is mounted
onMounted(() => {
  // Use nextTick to ensure the DOM is fully rendered before accessing element heights
  nextTick(() => {
    initializeFaqs()
  })
})
</script>

<template>
  <ContentView>
    <template #top>
      <!-- <DataRefreshDatesButton/> -->
      <ExternalLinks />
      <SalesRepContactButton label="Contact your Arrotex Rep" type="arrotex" />
      <SalesRepContactButton label="Contact your VidaCorp Rep" type="vidacorp" />
    </template>
    <template #middle>
      <div class="faqs">
        <h1 class="faq-title">Frequently Asked Questions</h1>

        <div class="faq-container">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="faq-item"
            :class="{ active: openIndex === index }"
            ref="faqItems"
          >
            <div class="faq-question" @click="toggleFaq(index)">
              <h3>{{ faq.question }}</h3>
              <span class="faq-icon">
                {{ openIndex === index ? '−' : '+' }}
              </span>
            </div>
            <div class="faq-answer">
              <p v-html="faq.answer"></p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </ContentView>
</template>

<style scoped>
.faqs {
  margin: 0 auto;
  padding: 1.25rem 1.25rem 2.5rem 1.25rem;
  width: 100%;
}

.faq-title {
  text-align: start;
  margin-bottom: 1.25rem;
  font-size: var(--font-title-1);
  color: var(--color-text);
}

.faq-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.faq-item {
  border-radius: 3.125rem;
  background-color: var(--color-background-bright);
  color: var(--color-text);
  width: 100%;
  overflow: hidden;
  transition:
    height 0.3s ease,
    background-color 0.3s ease,
    color 0.3s ease;
  height: 4.6875rem; /* Default closed height */
}

.faq-item.active {
  background-color: var(--color-text);
  color: var(--color-background-bright);
}

.faq-question {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem 1.875rem;
  cursor: pointer;
  height: 4.6875rem; /* Match the default closed height */
}

.faq-question h3 {
  margin: 0;
  font-size: var(--font-size-body);
  font-weight: 600;
}

.faq-icon {
  font-size: var(--font-title-2);
  font-weight: 300;
  transition: transform 0.3s ease;
}

.active .faq-icon {
  transform: rotate(180deg);
}

.faq-answer {
  padding: 0 1.875rem 1.875rem 1.875rem;
  line-height: 1.6;
  text-align: start;
  font-size: var(--font-size-body);
}

.faq-answer p {
  margin: 0;
  text-align: start;
  width: 100%;
}
</style>
